import { z } from "zod";
import { LookupListPage } from "./LookupListPage";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

interface Row {
  ntfTypeCode: string; description: string;
  emailEnabled: boolean; smsEnabled: boolean;
  emailTemplateId: number | null; smsTemplateId: number | null;
  triggeredByStatusCodeId: number | null;
  destination: string | null;
  destinationDepartmentId: number | null;
  autoAttachJobFileCategories: string | null;
  sendEmailFromAddressType: string | null;
  deleted: boolean;
}
const schema = z.object({
  ntfTypeCode: z.string().min(1).max(40),
  description: z.string().min(1).max(200),
  emailEnabled: z.boolean(),
  smsEnabled: z.boolean(),
  emailTemplateId: z.number().int().nullable().optional(),
  smsTemplateId: z.number().int().nullable().optional(),
  triggeredByStatusCodeId: z.number().int().nullable().optional(),
  destination: z.string().max(40).nullable().optional(),
  destinationDepartmentId: z.number().int().nullable().optional(),
  autoAttachJobFileCategories: z.string().nullable().optional(),
  sendEmailFromAddressType: z.string().max(20).nullable().optional(),
  deleted: z.boolean(),
});
type Form = z.infer<typeof schema>;

export function NotificationTypesPage() {
  return <LookupListPage<Row, Form>
    title="Notification types"
    apiPath="/api/lookups/notification-types"
    schema={schema}
    rowKey={(r) => r.ntfTypeCode}
    columns={[
      { id: "code", header: "Code", accessor: (r) => r.ntfTypeCode },
      { id: "desc", header: "Description", accessor: (r) => r.description },
      { id: "email", header: "Email", accessor: (r) => r.emailEnabled ? "Yes" : "—" },
      { id: "sms",  header: "SMS", accessor: (r) => r.smsEnabled ? "Yes" : "—" },
      { id: "dest", header: "Destination", accessor: (r) => r.destination ?? "—" },
    ]}
    toFormValues={(r) => ({
      ntfTypeCode: r.ntfTypeCode, description: r.description,
      emailEnabled: r.emailEnabled, smsEnabled: r.smsEnabled,
      emailTemplateId: r.emailTemplateId, smsTemplateId: r.smsTemplateId,
      triggeredByStatusCodeId: r.triggeredByStatusCodeId,
      destination: r.destination ?? "",
      destinationDepartmentId: r.destinationDepartmentId,
      autoAttachJobFileCategories: r.autoAttachJobFileCategories ?? "",
      sendEmailFromAddressType: r.sendEmailFromAddressType ?? "",
      deleted: r.deleted,
    })}
    toApiBody={(f) => f}
    blankFormValues={{
      ntfTypeCode: "", description: "", emailEnabled: false, smsEnabled: false,
      emailTemplateId: null, smsTemplateId: null, triggeredByStatusCodeId: null,
      destination: "", destinationDepartmentId: null,
      autoAttachJobFileCategories: "", sendEmailFromAddressType: "", deleted: false,
    }}
    fields={(f) => (
      <div className="space-y-3">
        <div><Label htmlFor="c">Code</Label><Input id="c" {...f.register("ntfTypeCode")} /></div>
        <div><Label htmlFor="d">Description</Label><Input id="d" {...f.register("description")} /></div>
        <label className="flex items-center gap-2"><input type="checkbox" {...f.register("emailEnabled")} /> Email enabled</label>
        <label className="flex items-center gap-2"><input type="checkbox" {...f.register("smsEnabled")} /> SMS enabled</label>
        <div><Label htmlFor="et">Email template ID</Label><Input id="et" type="number" {...f.register("emailTemplateId", { valueAsNumber: true })} /></div>
        <div><Label htmlFor="st">SMS template ID</Label><Input id="st" type="number" {...f.register("smsTemplateId", { valueAsNumber: true })} /></div>
        <div><Label htmlFor="ts">Triggered by status code ID</Label><Input id="ts" type="number" {...f.register("triggeredByStatusCodeId", { valueAsNumber: true })} /></div>
        <div><Label htmlFor="dest">Destination</Label><Input id="dest" {...f.register("destination")} /></div>
        <div><Label htmlFor="dd">Destination dept ID</Label><Input id="dd" type="number" {...f.register("destinationDepartmentId", { valueAsNumber: true })} /></div>
        <div><Label htmlFor="aaj">Auto-attach categories</Label><Input id="aaj" {...f.register("autoAttachJobFileCategories")} /></div>
        <div><Label htmlFor="sft">Send-from address type</Label><Input id="sft" {...f.register("sendEmailFromAddressType")} /></div>
        <Button type="submit">Save</Button>
      </div>
    )}
  />;
}
